import { PARTNER_KEYS } from "../../utils/partner";

// V2 design tokens — the ONLY styling channel V2 components may vary by partner.
// `--v2-color-text-strong` is the neutral black used where a design wants text
// to read black rather than in the partner's brand colour; a partner whose
// design prefers a near-black overrides just that entry.
// `--v2-color-price` is separate from `--v2-color-accent` because the accent is
// already in use elsewhere (profile) — only Clalit's value is design-approved.
// `--v2-color-icon-badge` is the tinted disc behind small list icons; only
// Clalit's value is design-approved.
// `--v2-color-banner-soft-bg` / `--v2-color-banner-soft-badge` are the tinted
// informational banner (home expired-pass card) and its icon square; only
// Clalit's values are design-approved.
// `--v2-gradient-pass-*` is the pass-promo gradient (the /how-it-works hero);
// Clalit's stops are the pass artwork's own so hero and card read as one object.
// Injected on :root by components/ThemeProvider.jsx alongside the legacy vars.
// V2 SCSS must reference only --v2-* variables (plus non-brand literals).
//
// Clalit values come from the approved V2 designs. Other partners currently
// carry placeholder values derived from their existing brand palette — replace
// each column when that partner's V2 designs arrive.
export const v2Tokens = {
  [PARTNER_KEYS.CLALIT]: {
    "--v2-color-primary": "#1B296B",
    "--v2-color-on-primary": "#FFFFFF",
    "--v2-color-text": "#1B296B",
    "--v2-color-text-secondary": "#959594",
    "--v2-color-text-strong": "#000000",
    "--v2-color-border": "#1B296B",
    "--v2-color-surface": "#FFFFFF",
    "--v2-color-surface-alt": "#F5F6F8",
    "--v2-color-accent": "#139deb",
    "--v2-color-price": "#0048D3",
    "--v2-color-icon-badge": "#C7DAFF",
    "--v2-color-banner-soft-bg": "#D3DAF6",
    "--v2-color-banner-soft-badge": "#B0BBEE",
    "--v2-gradient-hero-from": "#EFF1FC",
    "--v2-gradient-hero-mid": "#C6D1F6",
    "--v2-gradient-hero-to": "#4C6FE0",
    "--v2-gradient-pass-from": "#25B24D",
    "--v2-gradient-pass-mid": "#187F6E",
    "--v2-gradient-pass-to": "#253982",
    "--v2-radius-pill": "999px",
    "--v2-radius-button": "12px",
    "--v2-radius-card": "8px",
    "--v2-radius-menu": "16px",
    "--v2-radius-dialog": "24px",
    "--v2-font-family": `"OpenSans", sans-serif`,
  },
  [PARTNER_KEYS.MEHUDET]: {
    "--v2-color-primary": "#009BDF",
    "--v2-color-on-primary": "#FFFFFF",
    "--v2-color-text": "#003D62",
    "--v2-color-text-secondary": "#959594",
    "--v2-color-text-strong": "#000000",
    "--v2-color-border": "#003D62",
    "--v2-color-surface": "#FFFFFF",
    "--v2-color-surface-alt": "#F5F6F8",
    "--v2-color-accent": "#0091EA",
    "--v2-color-price": "#0091EA",
    "--v2-color-icon-badge": "#C4E9F9",
    "--v2-color-banner-soft-bg": "#D9F0FB",
    "--v2-color-banner-soft-badge": "#A8DDF4",
    "--v2-gradient-hero-from": "#EAF7FD",
    "--v2-gradient-hero-mid": "#A8DDF4",
    "--v2-gradient-hero-to": "#009BDF",
    "--v2-gradient-pass-from": "#25B24D",
    "--v2-gradient-pass-mid": "#0E7B9E",
    "--v2-gradient-pass-to": "#003D62",
    "--v2-radius-pill": "999px",
    "--v2-radius-button": "12px",
    "--v2-radius-card": "8px",
    "--v2-radius-menu": "16px",
    "--v2-radius-dialog": "24px",
    "--v2-font-family": `Ploni ML v2 AAA`,
  },
  [PARTNER_KEYS.LEUMIT]: {
    "--v2-color-primary": "#8E03A6",
    "--v2-color-on-primary": "#FFFFFF",
    "--v2-color-text": "#8E03A6",
    "--v2-color-text-secondary": "#959594",
    "--v2-color-text-strong": "#000000",
    "--v2-color-border": "#8E03A6",
    "--v2-color-surface": "#FFFFFF",
    "--v2-color-surface-alt": "#F5F6F8",
    "--v2-color-accent": "#8E03A6",
    "--v2-color-price": "#8E03A6",
    "--v2-color-icon-badge": "#EBCDF1",
    "--v2-color-banner-soft-bg": "#F2DDF6",
    "--v2-color-banner-soft-badge": "#DCB0E6",
    "--v2-gradient-hero-from": "#F7EAFA",
    "--v2-gradient-hero-mid": "#DCB0E6",
    "--v2-gradient-hero-to": "#8E03A6",
    "--v2-gradient-pass-from": "#25B24D",
    "--v2-gradient-pass-mid": "#5C4A9E",
    "--v2-gradient-pass-to": "#8E03A6",
    "--v2-radius-pill": "999px",
    "--v2-radius-button": "12px",
    "--v2-radius-card": "8px",
    "--v2-radius-menu": "16px",
    "--v2-radius-dialog": "24px",
    "--v2-font-family": `"OpenSans", sans-serif`,
  },
  [PARTNER_KEYS.OVDIM_BARI]: {
    "--v2-color-primary": "#3946C0",
    "--v2-color-on-primary": "#FFFFFF",
    "--v2-color-text": "#3946C0",
    "--v2-color-text-secondary": "#959594",
    "--v2-color-text-strong": "#000000",
    "--v2-color-border": "#3946C0",
    "--v2-color-surface": "#FFFFFF",
    "--v2-color-surface-alt": "#F5F6F8",
    "--v2-color-accent": "#3946C0",
    "--v2-color-price": "#3946C0",
    "--v2-color-icon-badge": "#CFD4F4",
    "--v2-color-banner-soft-bg": "#DDE1F8",
    "--v2-color-banner-soft-badge": "#B9C0EC",
    "--v2-gradient-hero-from": "#EEF0FB",
    "--v2-gradient-hero-mid": "#B9C0EC",
    "--v2-gradient-hero-to": "#3946C0",
    "--v2-gradient-pass-from": "#25B24D",
    "--v2-gradient-pass-mid": "#2E7A8E",
    "--v2-gradient-pass-to": "#3946C0",
    "--v2-radius-pill": "999px",
    "--v2-radius-button": "12px",
    "--v2-radius-card": "8px",
    "--v2-radius-menu": "16px",
    "--v2-radius-dialog": "24px",
    "--v2-font-family": `"OpenSans", sans-serif`,
  },
  [PARTNER_KEYS.MAOFLAAMIT]: {
    "--v2-color-primary": "#F7941D",
    "--v2-color-on-primary": "#FFFFFF",
    "--v2-color-text": "#333333",
    "--v2-color-text-secondary": "#959594",
    "--v2-color-text-strong": "#000000",
    "--v2-color-border": "#333333",
    "--v2-color-surface": "#FFFFFF",
    "--v2-color-surface-alt": "#F5F6F8",
    "--v2-color-accent": "#F7941D",
    "--v2-color-price": "#F7941D",
    "--v2-color-icon-badge": "#FDE3C4",
    "--v2-color-banner-soft-bg": "#FEF0DE",
    "--v2-color-banner-soft-badge": "#FBD6A6",
    "--v2-gradient-hero-from": "#FEF5EA",
    "--v2-gradient-hero-mid": "#FBD6A6",
    "--v2-gradient-hero-to": "#F7941D",
    "--v2-gradient-pass-from": "#25B24D",
    "--v2-gradient-pass-mid": "#8FA83A",
    "--v2-gradient-pass-to": "#F7941D",
    "--v2-radius-pill": "999px",
    "--v2-radius-button": "12px",
    "--v2-radius-card": "8px",
    "--v2-radius-menu": "16px",
    "--v2-radius-dialog": "24px",
    "--v2-font-family": `"OpenSans", sans-serif`,
  },
};
